// src/pages/Dashboard.tsx
import React, { useState } from 'react';
import { Card, Row, Col, Statistic, Space, Typography, Button } from 'antd';
import { UserOutlined, CalendarOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { doctorLinkAPI } from '../utils/api';
import { DiaryProgressDashboard } from '../components/DiaryProgressDashboard';
import { DoctorDashboard } from './DoctorDashboard';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { getCurrentWeekOfPregnancy } from '../utils/pregnancyUtils';
import { useIsMobile } from '../hooks/useIsMobile';

const { Title, Text } = Typography;

type DoctorLinkDisplay = {
  label: string;
  status: 'none' | 'pending' | 'active' | 'loading';
};

function doctorLabelFromBundle(bundle: any): DoctorLinkDisplay {
  const status = bundle?.link?.status;
  if (status === 'pending') {
    return { label: 'Заявка на рассмотрении', status: 'pending' };
  }
  if (status === 'active') {
    return {
      label: bundle?.doctor_full_name || 'Назначен',
      status: 'active',
    };
  }
  return { label: 'Не назначен', status: 'none' };
}

export const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [doctorDisplay, setDoctorDisplay] = useState<DoctorLinkDisplay>({
    label: 'Загрузка...',
    status: 'loading',
  });

  React.useEffect(() => {
    const loadDoctorLink = async () => {
      if (user?.role !== 'patient') {
        setDoctorDisplay({ label: '', status: 'none' });
        return;
      }
      try {
        setDoctorDisplay({ label: 'Загрузка...', status: 'loading' });
        const response = await doctorLinkAPI.getDoctorLinkState();
        const bundle = (response.data as any)?.bundle;
        setDoctorDisplay(doctorLabelFromBundle(bundle));
      } catch (error) {
        console.error('Error loading doctor link state:', error);
        setDoctorDisplay({
          label: user?.doctor_id ? 'Назначен' : 'Не назначен',
          status: user?.doctor_id ? 'active' : 'none',
        });
      }
    };
    loadDoctorLink();
  }, [user?.id, user?.role, user?.doctor_id]);

  if (user?.role === 'doctor') {
    return <DoctorDashboard />;
  }

  const firstName = user?.full_name?.split(' ')[1] || 'подруга';

  return (
    <div className="patient-dashboard" style={{ maxWidth: 1100, margin: '0 auto', width: '100%' }}>
      <div style={{ marginBottom: isMobile ? 16 : 24 }}>
        <Title
          level={isMobile ? 3 : 2}
          style={{ color: '#263238', marginBottom: 8, fontSize: isMobile ? 20 : 24 }}
        >
          Добро пожаловать, {firstName}!
        </Title>
        <Text style={{ color: '#5A7B8C', fontSize: isMobile ? 13 : 14 }}>
          Ваше здоровье и благополучие — наш главный приоритет
        </Text>
      </div>

      <Space direction="vertical" size={isMobile ? 'middle' : 'large'} style={{ width: '100%' }}>
        <Card
          style={{
            borderRadius: 12,
            backgroundColor: '#F0F9F4',
            border: '1px solid #e8eee9',
            textAlign: 'center',
          }}
          bodyStyle={{ padding: isMobile ? 16 : 20 }}
        >
          <Text style={{ fontSize: isMobile ? 16 : 18, fontWeight: 500, color: '#263238' }}>
            Сегодня {format(new Date(), 'dd MMMM yyyy', { locale: ru })}
          </Text>
        </Card>

        <Card
          style={{
            borderRadius: 12,
            backgroundColor: '#ffffff',
            border: '1px solid #e8eee9',
          }}
          bodyStyle={{ padding: isMobile ? 16 : 24 }}
        >
          <Row gutter={[16, 20]} align="middle">
            <Col xs={24} sm={12} lg={6}>
              <Statistic
                title="Неделя беременности"
                value={user ? getCurrentWeekOfPregnancy(user) || 0 : 0}
                suffix="неделя"
                prefix={<UserOutlined style={{ color: '#7CB342' }} />}
                valueStyle={{ color: '#263238', fontSize: isMobile ? 20 : 22 }}
              />
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Statistic
                title="ПДР"
                value={user?.due_date ? format(new Date(user.due_date), 'dd.MM.yyyy') : 'Не указана'}
                prefix={<CalendarOutlined style={{ color: '#7CB342' }} />}
                valueStyle={{ color: '#263238', fontSize: isMobile ? 16 : 18 }}
              />
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Statistic
                title="Врач"
                value={doctorDisplay.label}
                prefix={<UserOutlined style={{ color: '#7CB342' }} />}
                valueStyle={{ color: '#263238', fontSize: isMobile ? 16 : 18 }}
              />
            </Col>
            <Col xs={24} sm={12} lg={6} style={{ textAlign: isMobile ? 'left' : 'center' }}>
              <Space direction={isMobile ? 'vertical' : 'horizontal'} style={{ width: isMobile ? '100%' : undefined }}>
                <Button
                  type="primary"
                  size="large"
                  block={isMobile}
                  onClick={() => navigate('/diary')}
                >
                  Дневники
                </Button>
                <Button size="large" block={isMobile} onClick={() => navigate('/profile')}>
                  Профиль
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>

        <DiaryProgressDashboard />

        <Card title="Советы по здоровью" style={{ borderRadius: 12 }} bodyStyle={{ padding: isMobile ? 16 : 24 }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <div style={{ padding: 12, background: '#FAFAFA', borderRadius: 8, height: '100%' }}>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>
                  Уровень сахара в крови
                </Text>
                <Text type="secondary">
                  Следите за уровнем глюкозы до и через 1–2 часа после еды. Норма: натощак 3.3–5.5
                  ммоль/л, после еды менее 7.8 ммоль/л.
                </Text>
              </div>
            </Col>
            <Col xs={24} md={12}>
              <div style={{ padding: 12, background: '#FAFAFA', borderRadius: 8, height: '100%' }}>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>
                  Питание при беременности
                </Text>
                <Text type="secondary">
                  Соблюдайте сбалансированное питание с достаточным количеством белков, полезных
                  жиров и сложных углеводов. Пейте достаточно воды.
                </Text>
              </div>
            </Col>
          </Row>
        </Card>
      </Space>
    </div>
  );
};
